'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import cheersMenuLogo from '~/assets/images/cheers_menu_logo.png';
import { useNav } from '~/contexts/NavContext';
import { AppMenu } from '~/shared/components/AppMenu';
import { HelpCenterDialog } from '~/shared/components/HelpCenterDialog';

import { useMenuListScroll } from '../../hooks/useMenuListScroll';

export function DesktopMenu() {
  const {
    isAppMenuOpen,
    subMenuOpen,
    menuItems,
    footerItems,
    scheduleOpenAppMenu,
    scheduleCloseAppMenu,
    keepAppMenuOpen,
    checkIfIsActive,
    toggleSubMenu,
  } = useNav();
  const router = useRouter();
  const [isHelpCenterDialogOpen, setIsHelpCenterDialogOpen] = useState(false);

  const toggleHelpCenterDialog = () => {
    setIsHelpCenterDialogOpen(prev => !prev);
  };

  const redirectToPage = (path: string, shouldOpenInNewTab?: boolean) => {
    if (shouldOpenInNewTab) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      router.push(path);
    }
  };

  const { elementRef, isScrollable } = useMenuListScroll();
  return (
    <>
      <AppMenu open={isAppMenuOpen}>
        <AppMenu.Trigger asChild>
          <AppMenu.Bar
            openMenu={scheduleOpenAppMenu}
            closeMenu={scheduleCloseAppMenu}
          >
            <AppMenu.Header />
            <AppMenu.MenuList>
              {menuItems.map(item => (
                <AppMenu.MenuItem
                  key={item.key}
                  leftIcon={item.icon}
                  tooltip={item.title}
                  isActive={checkIfIsActive(
                    item.path ?? '',
                    item.routeComponents
                  )}
                  isTriggerItem
                  showNotificationBadge={item.showNotificationBadge}
                />
              ))}
            </AppMenu.MenuList>
            <AppMenu.Footer>
              {footerItems.map(item => (
                <AppMenu.MenuItem
                  key={item.key}
                  leftIcon={item.icon}
                  tooltip={item.title}
                  isActive={checkIfIsActive(
                    item.path ?? '',
                    item.routeComponents
                  )}
                  isTriggerItem
                  showNotificationBadge={item.showNotificationBadge}
                />
              ))}
            </AppMenu.Footer>
          </AppMenu.Bar>
        </AppMenu.Trigger>
        <AppMenu.Content
          closeMenu={scheduleCloseAppMenu}
          keepOpen={keepAppMenuOpen}
        >
          <AppMenu.Header />
          <AppMenu.MenuList
            isMainList
            ref={elementRef}
            bottomElement={
              <Image src={cheersMenuLogo} placeholder="blur" alt="Cheers Hub" />
            }
            bottomElementClassName={
              isScrollable ? 'w-[calc(220px-0.5rem)]' : 'w-[220px]'
            }
          >
            {menuItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                route={item.path}
                title={item.title}
                leftIcon={item.icon}
                hasSubmenu={!!item.routeComponents?.length}
                isSubMenuOpen={subMenuOpen?.some(
                  menu => menu?.key === item.key
                )}
                onHover={() => toggleSubMenu(item, true)}
                onHoverDelay={100}
                isActive={checkIfIsActive(
                  item.path ?? '',
                  item.routeComponents
                )}
                onClick={() => item.path && router.push(item.path)}
                showNotificationBadge={item.showNotificationBadge}
                renderSubmenu={
                  <AppMenu.SubMenu
                    title={item.title || ''}
                    className="rounded-lg"
                    closeSubMenu={() => toggleSubMenu(null)}
                    keepOpen={keepAppMenuOpen}
                  >
                    <AppMenu.MenuList>
                      {item.routeComponents?.map(subitem => (
                        <AppMenu.MenuItem
                          key={subitem.key}
                          title={subitem.title}
                          route={subitem.path}
                          hasSubmenu={!!subitem.routeComponents?.length}
                          isSubMenuOpen={subMenuOpen?.some(
                            menu => menu?.key === subitem.key
                          )}
                          onHover={() => {
                            toggleSubMenu(subitem, false);
                          }}
                          onHoverDelay={300}
                          isActive={checkIfIsActive(
                            subitem.path ?? '',
                            subitem.routeComponents
                          )}
                          onClick={() =>
                            subitem.path && router.push(subitem.path)
                          }
                          renderSubmenu={
                            <AppMenu.SubMenu
                              title={subitem.title || ''}
                              className="rounded-lg"
                              closeSubMenu={() => toggleSubMenu(null)}
                              keepOpen={keepAppMenuOpen}
                            >
                              <AppMenu.MenuList>
                                {subitem.routeComponents?.map(otherItem => (
                                  <AppMenu.MenuItem
                                    key={otherItem.key}
                                    title={otherItem.title}
                                    route={otherItem.path}
                                    onClick={() =>
                                      otherItem.path &&
                                      router.push(otherItem.path)
                                    }
                                  />
                                ))}
                              </AppMenu.MenuList>
                            </AppMenu.SubMenu>
                          }
                        />
                      ))}
                    </AppMenu.MenuList>
                  </AppMenu.SubMenu>
                }
              />
            ))}
          </AppMenu.MenuList>
          <AppMenu.Footer>
            {footerItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                route={item.path}
                title={item.title}
                hasSubmenu={!!item.routeComponents?.length}
                leftIcon={item.icon}
                isSubMenuOpen={subMenuOpen?.some(
                  menu => menu?.key === item.key
                )}
                onHover={() => toggleSubMenu(item)}
                onHoverDelay={300}
                isActive={checkIfIsActive(
                  item.path ?? '',
                  item.routeComponents
                )}
                onClick={() => item.path && router.push(item.path)}
                showNotificationBadge={item.showNotificationBadge}
                renderSubmenu={
                  <AppMenu.SubMenu
                    title={item.title || ''}
                    className="rounded-lg"
                    closeSubMenu={() => toggleSubMenu(null)}
                    keepOpen={keepAppMenuOpen}
                  >
                    <AppMenu.MenuList>
                      {item.routeComponents?.map(subitem => (
                        <AppMenu.MenuItem
                          key={subitem.key}
                          title={subitem.title}
                          {...(subitem.path &&
                            subitem.path !== '' && { route: subitem.path })}
                          onClick={() => {
                            if (subitem.helpCenterDialog) {
                              toggleHelpCenterDialog();
                            } else if (subitem.path) {
                              redirectToPage(
                                subitem.path,
                                subitem.shouldOpenInNewTab
                              );
                            }
                          }}
                          isSubMenuItem
                        />
                      ))}
                    </AppMenu.MenuList>
                  </AppMenu.SubMenu>
                }
              />
            ))}
          </AppMenu.Footer>
        </AppMenu.Content>
      </AppMenu>
      <HelpCenterDialog
        open={isHelpCenterDialogOpen}
        setIsHelpCenterDialogOpen={toggleHelpCenterDialog}
      />
    </>
  );
}
